import React from 'react';
import { FileText, FileCheck, Hammer } from 'lucide-react';

interface DocumentType {
  id: string;
  name: string;
  subtitle: string;
  fieldCount: number;
  icon: React.ReactNode;
  color: string;
}

const DOCUMENT_TYPES: DocumentType[] = [
  {
    id: 'swms',
    name: 'Asbestos SWMS',
    subtitle: 'Licensed Removal Safe Work Method Statement',
    fieldCount: 9,
    icon: <FileText className="w-6 h-6" />,
    color: 'blue',
  },
  {
    id: 'arcp',
    name: 'Control Plan (ARCP)',
    subtitle: 'Asbestos Removal Control Plan',
    fieldCount: 10,
    icon: <FileCheck className="w-6 h-6" />,
    color: 'green',
  },
  {
    id: 'demo',
    name: 'Demolition SWMS',
    subtitle: 'Demolition Safe Work Method Statement',
    fieldCount: 8,
    icon: <Hammer className="w-6 h-6" />,
    color: 'red',
  },
];

interface DocumentSelectorProps {
  selectedDocs: Set<string>;
  onToggleDoc: (docId: string) => void;
  workerCounts: Record<string, number>;
}

export default function DocumentSelector({
  selectedDocs,
  onToggleDoc,
  workerCounts,
}: DocumentSelectorProps) {
  const handleSelectAll = () => {
    DOCUMENT_TYPES.forEach(doc => {
      if (!selectedDocs.has(doc.id)) {
        onToggleDoc(doc.id);
      }
    });
  };

  const handleClearAll = () => {
    DOCUMENT_TYPES.forEach(doc => {
      if (selectedDocs.has(doc.id)) {
        onToggleDoc(doc.id);
      }
    });
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <button
          onClick={handleSelectAll}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          ✓ Select All
        </button>
        <button
          onClick={handleClearAll}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          ✕ Clear All
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {DOCUMENT_TYPES.map(doc => {
          const isSelected = selectedDocs.has(doc.id);
          const workerCount = workerCounts[doc.id] || 0;

          const colorClasses = {
            blue: {
              border: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300',
              checkbox: isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 bg-white',
              icon: 'text-blue-600',
              badge: 'bg-blue-100 text-blue-800',
            },
            green: {
              border: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300',
              checkbox: isSelected ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 bg-white',
              icon: 'text-green-600',
              badge: 'bg-green-100 text-green-800',
            },
            red: {
              border: isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-red-300',
              checkbox: isSelected ? 'bg-red-500 border-red-500 text-white' : 'border-gray-300 bg-white',
              icon: 'text-red-600',
              badge: 'bg-red-100 text-red-800',
            },
          };

          const colors = colorClasses[doc.color as keyof typeof colorClasses];

          return (
            <button
              key={doc.id}
              onClick={() => onToggleDoc(doc.id)}
              className={`relative p-4 rounded-lg border-2 transition-all text-left ${colors.border}`}
            >
              <div className="absolute top-3 right-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center text-xs font-bold transition-colors ${colors.checkbox}`}
                >
                  {isSelected && '✓'}
                </div>
              </div>

              <div className={`mb-2 ${colors.icon}`}>{doc.icon}</div>

              <h3 className="font-bold text-sm text-gray-900 mb-1">{doc.name}</h3>
              <p className="text-xs text-gray-600 leading-tight mb-2">{doc.subtitle}</p>

              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${colors.badge}`}>
                  {doc.fieldCount} fields
                </span>
                {isSelected && workerCount > 0 && doc.id !== 'arcp' && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                    {workerCount} worker{workerCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
